import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { Alert, Button, CircularProgress } from "@mui/material";
import {
  CheckoutElementsProvider,
  PaymentElement,
  useCheckoutElements,
} from "@stripe/react-stripe-js/checkout";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import React, { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import {
  invoiceDesignTokens,
  invoiceModalActionSx,
} from "./InvoiceDesignSystem";
import { useCmpOrganizationNoun } from "../../../contexts/CmpOrganizationNounContext";
import { applyCmpOrganizationNoun } from "../../../utils/cmpOrganizationNoun";
import {
  buildStaffErrorFallback,
  getUserFacingErrorText,
  UserFacingError,
} from "../../../utils/userFacingError";

/**
 * IDLE: no card submit is open. SUBMITTING: Stripe is confirming the card,
 * including a 3D Secure challenge. SUBMITTED: Stripe accepted the submit (or
 * returned no result), and the invoice has not shown the payment yet.
 */
export type StripeCardSubmissionState = "IDLE" | "SUBMITTING" | "SUBMITTED";

export type StripeCheckoutSessionPaymentElementProps = {
  amountInCents: number;
  /** Shown above Confirm & Record Payment, for example Internal Notes (design). */
  beforeSubmit?: React.ReactNode;
  checkoutSessionId: string;
  clientSecret: string;
  connectedAccountId: string;
  disabled?: boolean;
  /** Runs before Stripe confirms the card; a throw stops the submit and
   * shows its message (for example a note that could not be saved). */
  onBeforeConfirm?: () => Promise<void>;
  onPaymentElementUnavailable: (message: string) => void;
  onPaymentSubmitted: () => Promise<void> | void;
  /** Reports each card submit state so the popup can lock while it is open. */
  onSubmissionStateChange?: (state: StripeCardSubmissionState) => void;
  publishableKey: string;
  returnUrl: string;
};

const publishableKeyPattern = /^pk_(?:test|live)_[A-Za-z0-9]+$/;
const connectedAccountIdPattern = /^acct_[A-Za-z0-9]+$/;
const checkoutSessionIdPattern = /^cs_(?:test|live)_[A-Za-z0-9]+$/;

const stripePromisesByConfiguration = new Map<string, Promise<Stripe | null>>();

const isLocalHostname = (hostname: string) =>
  hostname === "localhost" || hostname === "127.0.0.1" || hostname === "[::1]";

export const isSafeStripeCheckoutReturnUrl = (
  returnUrl: string,
  currentOrigin = typeof window === "undefined" ? "" : window.location.origin,
) => {
  try {
    if (!currentOrigin) {
      return false;
    }

    const parsedReturnUrl = new URL(returnUrl);
    const parsedCurrentOrigin = new URL(currentOrigin);
    const protocolIsAllowed =
      parsedReturnUrl.protocol === "https:" ||
      (parsedReturnUrl.protocol === "http:" &&
        isLocalHostname(parsedReturnUrl.hostname));

    return (
      protocolIsAllowed &&
      parsedReturnUrl.origin === parsedCurrentOrigin.origin &&
      !parsedReturnUrl.username &&
      !parsedReturnUrl.password &&
      !parsedReturnUrl.hash
    );
  } catch {
    return false;
  }
};

// The panel below the form adds the steps (Close Process Payment ...).
const CARD_FORM_NOT_OPENED = "The card form could not open, so no payment was taken.";
// A key or connected account that does not match means card payments are
// not set up for this chamber. The noun is swapped where it is shown.
const CARD_PAYMENTS_NOT_SET_UP =
  "Card payments are not set up for your chamber, so no payment was taken.";
const PAYMENT_STATUS_WAIT_STEPS =
  "We check the invoice every few seconds. If the status does not change in 2 minutes, close Process Payment and open it again.";

export const getStripeCheckoutSessionConfigurationError = ({
  amountInCents,
  checkoutSessionId,
  clientSecret,
  connectedAccountId,
  publishableKey,
  returnUrl,
}: Pick<
  StripeCheckoutSessionPaymentElementProps,
  | "amountInCents"
  | "checkoutSessionId"
  | "clientSecret"
  | "connectedAccountId"
  | "publishableKey"
  | "returnUrl"
>): string | null => {
  if (!Number.isSafeInteger(amountInCents) || amountInCents <= 0) {
    return CARD_FORM_NOT_OPENED;
  }

  if (!checkoutSessionIdPattern.test(checkoutSessionId)) {
    return CARD_FORM_NOT_OPENED;
  }

  // Stripe owns the client-secret format. Keep it opaque here so a valid
  // provider value is not rejected before Stripe.js can validate it.
  if (!clientSecret.trim()) {
    return CARD_FORM_NOT_OPENED;
  }

  if (!publishableKeyPattern.test(publishableKey)) {
    return CARD_PAYMENTS_NOT_SET_UP;
  }

  if (!connectedAccountIdPattern.test(connectedAccountId)) {
    return CARD_PAYMENTS_NOT_SET_UP;
  }

  if (!isSafeStripeCheckoutReturnUrl(returnUrl)) {
    return CARD_FORM_NOT_OPENED;
  }

  return null;
};

const getStripePromise = ({
  connectedAccountId,
  publishableKey,
}: {
  connectedAccountId: string;
  publishableKey: string;
}) => {
  const configurationKey = `${publishableKey}:${connectedAccountId}`;
  const existingPromise = stripePromisesByConfiguration.get(configurationKey);

  if (existingPromise) {
    return existingPromise;
  }

  const stripePromise = Promise.resolve(
    loadStripe(publishableKey, {
      stripeAccount: connectedAccountId,
    }),
  ).then(
    (stripe) => {
      if (!stripe) {
        stripePromisesByConfiguration.delete(configurationKey);
      }

      return stripe;
    },
    (error: unknown) => {
      stripePromisesByConfiguration.delete(configurationKey);
      throw error;
    },
  );
  stripePromisesByConfiguration.set(configurationKey, stripePromise);

  return stripePromise;
};

const STRIPE_CONFIRM_FALLBACK = buildStaffErrorFallback(
  "take the card payment",
  "No payment was taken. Check the card details.",
);
const CARD_NOTE_SAVE_FALLBACK = buildStaffErrorFallback(
  "save the internal note",
  "No payment was submitted.",
);

// Stripe card messages ("Your card was declined.") are written for card
// holders and can be shown. Text with keys, ids, links or setup words is
// technical, so staff see the fallback instead.
const TECHNICAL_STRIPE_TEXT =
  /\b(?:cs|pk|sk|pi|seti|acct)_(?:test_|live_)?[a-z0-9]+|https?:\/\/|\b(?:api|secret|publishable|integration|client|undefined|null|session|stripe\.js)\b/i;

export const getStripeConfirmErrorText = (message?: string | null) => {
  const trimmedMessage = (message || "").trim();
  const shownError =
    trimmedMessage && !TECHNICAL_STRIPE_TEXT.test(trimmedMessage)
      ? new UserFacingError(trimmedMessage)
      : null;

  return getUserFacingErrorText(shownError, STRIPE_CONFIRM_FALLBACK);
};

type StripePaymentFormProps = Pick<
  StripeCheckoutSessionPaymentElementProps,
  | "amountInCents"
  | "beforeSubmit"
  | "disabled"
  | "onBeforeConfirm"
  | "onPaymentElementUnavailable"
  | "onPaymentSubmitted"
  | "onSubmissionStateChange"
>;

// The backend sets return_url when it creates the Basil Custom Checkout
// Session. Stripe rejects a second returnUrl during confirmation.
export const getStripeCheckoutConfirmOptions = () => ({
  redirect: "if_required" as const,
});

export const stripeCheckoutTotalMatchesExpectedAmount = ({
  expectedAmountInCents,
  stripeFormattedAmount,
  stripeAmountInMinorUnits,
}: {
  expectedAmountInCents: number;
  stripeFormattedAmount: string;
  stripeAmountInMinorUnits: number;
}) =>
  Number.isSafeInteger(expectedAmountInCents) &&
  expectedAmountInCents > 0 &&
  Number.isSafeInteger(stripeAmountInMinorUnits) &&
  stripeAmountInMinorUnits === expectedAmountInCents &&
  Boolean(stripeFormattedAmount.trim());

export const canSubmitStripeCheckoutSessionPayment = ({
  checkoutCanConfirm,
  checkoutReady,
  disabled,
  elementComplete,
  elementReady,
  submitting,
}: {
  checkoutCanConfirm: boolean;
  checkoutReady: boolean;
  disabled: boolean;
  elementComplete: boolean;
  elementReady: boolean;
  submitting: boolean;
}) =>
  checkoutReady &&
  checkoutCanConfirm &&
  elementReady &&
  elementComplete &&
  !disabled &&
  !submitting;

export const StripePaymentForm = ({
  amountInCents,
  beforeSubmit,
  disabled = false,
  onBeforeConfirm,
  onPaymentElementUnavailable,
  onPaymentSubmitted,
  onSubmissionStateChange,
}: StripePaymentFormProps) => {
  const checkoutState = useCheckoutElements();
  const stripeCheckoutTotal =
    checkoutState.type === "success"
      ? checkoutState.checkout.total.total
      : null;
  const checkoutTotalMatchesExpectedAmount = stripeCheckoutTotal
    ? stripeCheckoutTotalMatchesExpectedAmount({
        expectedAmountInCents: amountInCents,
        stripeAmountInMinorUnits: stripeCheckoutTotal.minorUnitsAmount,
        stripeFormattedAmount: stripeCheckoutTotal.amount,
      })
    : false;
  const [elementComplete, setElementComplete] = useState(false);
  const [elementReady, setElementReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submissionMessage, setSubmissionMessage] = useState<string | null>(
    null,
  );
  const [submissionLocked, setSubmissionLocked] = useState(false);
  const unavailableReportedRef = useRef(false);
  const submissionInFlightRef = useRef(false);

  const reportUnavailable = (message: string) => {
    if (unavailableReportedRef.current) {
      return;
    }

    unavailableReportedRef.current = true;
    onPaymentElementUnavailable(message);
  };

  useEffect(() => {
    if (checkoutState.type === "error") {
      reportUnavailable(CARD_FORM_NOT_OPENED);
    }
    // The callback is intentionally reported once for this mounted Session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutState.type]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (checkoutState.type !== "success") {
      return;
    }

    if (
      !canSubmitStripeCheckoutSessionPayment({
        checkoutCanConfirm: checkoutState.checkout.canConfirm,
        checkoutReady: true,
        disabled:
          disabled || submissionLocked || !checkoutTotalMatchesExpectedAmount,
        elementComplete,
        elementReady,
        submitting,
      }) ||
      submissionInFlightRef.current
    ) {
      return;
    }

    submissionInFlightRef.current = true;
    setSubmitting(true);
    setSubmissionMessage(null);
    onSubmissionStateChange?.("SUBMITTING");

    if (onBeforeConfirm) {
      try {
        await onBeforeConfirm();
      } catch (error) {
        setSubmissionMessage(
          getUserFacingErrorText(error, CARD_NOTE_SAVE_FALLBACK),
        );
        submissionInFlightRef.current = false;
        setSubmitting(false);
        onSubmissionStateChange?.("IDLE");
        return;
      }
    }

    try {
      const result = await checkoutState.checkout.confirm(
        getStripeCheckoutConfirmOptions(),
      );

      if (result.type === "error") {
        // A decline or a failed or cancelled 3D Secure check. No charge was
        // made, so staff can change the card, amount or method again.
        setSubmissionMessage(getStripeConfirmErrorText(result.error.message));
        onSubmissionStateChange?.("IDLE");
        return;
      }

      setSubmissionLocked(true);
      onSubmissionStateChange?.("SUBMITTED");
      setSubmissionMessage(
        "Payment submitted. Keep this window open while we wait for Stripe to confirm the payment.",
      );

      try {
        await onPaymentSubmitted();
      } catch {
        setSubmissionMessage(
          `The payment was sent. Do not take the payment again. ${PAYMENT_STATUS_WAIT_STEPS}`,
        );
      }
    } catch {
      setSubmissionLocked(true);
      onSubmissionStateChange?.("SUBMITTED");
      setSubmissionMessage(
        `We could not get the payment result. Do not take the payment again. ${PAYMENT_STATUS_WAIT_STEPS}`,
      );
    } finally {
      submissionInFlightRef.current = false;
      setSubmitting(false);
    }
  };

  if (checkoutState.type === "error") {
    return (
      <Alert severity="error">
        {CARD_FORM_NOT_OPENED}
      </Alert>
    );
  }

  return (
    <form onSubmit={(event) => void handleSubmit(event)}>
      <div aria-busy={!elementReady} className="relative min-h-[132px]">
        {!elementReady ? (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg border border-gray-200 bg-white">
            <CircularProgress aria-label="Loading secure card form" size={24} />
          </div>
        ) : null}
        <PaymentElement
          onChange={(event) => {
            setElementComplete(event.complete);
            if (!submissionLocked && submissionMessage) {
              setSubmissionMessage(null);
            }
          }}
          onLoadError={() => {
            setElementReady(false);
            reportUnavailable(CARD_FORM_NOT_OPENED);
          }}
          onReady={() => setElementReady(true)}
          options={{ layout: "tabs", readOnly: submissionLocked }}
        />
      </div>

      {stripeCheckoutTotal ? (
        <div
          className="mt-3 flex items-center justify-between rounded-lg bg-[#F4F8FB] px-3 py-2 text-[12px] text-[#0A0A0A]"
          data-testid="stripe-checkout-total"
        >
          <span>Stripe payment total</span>
          <strong>{stripeCheckoutTotal.amount}</strong>
        </div>
      ) : null}

      {stripeCheckoutTotal && !checkoutTotalMatchesExpectedAmount ? (
        <Alert className="mt-3" severity="error">
          Stripe returned a payment total that does not match this invoice.
          Close this popup and open Add Payment again. No payment was submitted.
        </Alert>
      ) : null}

      {submissionMessage ? (
        <Alert
          className="mt-4"
          severity={
            submissionMessage.startsWith("Payment submitted") ? "info" : "error"
          }
        >
          <span aria-live="polite">{submissionMessage}</span>
        </Alert>
      ) : null}

      {beforeSubmit ? <div className="mt-4">{beforeSubmit}</div> : null}
      <Button
        disabled={
          checkoutState.type !== "success" ||
          !canSubmitStripeCheckoutSessionPayment({
            checkoutCanConfirm:
              checkoutState.type === "success"
                ? checkoutState.checkout.canConfirm
                : false,
            checkoutReady: checkoutState.type === "success",
            disabled:
              disabled ||
              submissionLocked ||
              !checkoutTotalMatchesExpectedAmount,
            elementComplete,
            elementReady,
            submitting,
          })
        }
        fullWidth
        startIcon={
          submitting ? (
            <CircularProgress color="inherit" size={18} />
          ) : (
            <LockOutlinedIcon />
          )
        }
        sx={{
          ...invoiceModalActionSx({
            backgroundColor: invoiceDesignTokens.blue,
            hoverBackgroundColor: "#0079D6",
          }),
          fontSize: 14,
          minHeight: 42,
          mt: 2.5,
        }}
        type="submit"
        variant="contained"
      >
        {submitting ? "Submitting payment..." : "Confirm & Record Payment"}
      </Button>
    </form>
  );
};

const StripeCheckoutSessionPaymentElement = (
  props: StripeCheckoutSessionPaymentElementProps,
) => {
  const noun = useCmpOrganizationNoun();
  const defaultConfigurationError =
    getStripeCheckoutSessionConfigurationError(props);
  const configurationError = defaultConfigurationError
    ? applyCmpOrganizationNoun(defaultConfigurationError, noun)
    : defaultConfigurationError;
  const stripePromise = useMemo(
    () =>
      configurationError
        ? null
        : getStripePromise({
            connectedAccountId: props.connectedAccountId,
            publishableKey: props.publishableKey,
          }),
    [configurationError, props.connectedAccountId, props.publishableKey],
  );
  const configurationErrorReportedRef = useRef<string | null>(null);
  const { onPaymentElementUnavailable } = props;

  useEffect(() => {
    if (
      configurationError &&
      configurationErrorReportedRef.current !== configurationError
    ) {
      configurationErrorReportedRef.current = configurationError;
      onPaymentElementUnavailable(configurationError);
    }
  }, [configurationError, onPaymentElementUnavailable]);

  if (configurationError || !stripePromise) {
    return <Alert severity="error">{configurationError}</Alert>;
  }

  return (
    <CheckoutElementsProvider
      key={props.checkoutSessionId}
      options={{
        clientSecret: props.clientSecret,
        elementsOptions: {
          appearance: {
            theme: "stripe",
            variables: {
              borderRadius: "8px",
              colorPrimary: "#0091FF",
              colorText: "#0A0A0A",
              fontFamily: '"DM Sans", sans-serif',
            },
          },
          loader: "auto",
        },
      }}
      stripe={stripePromise}
    >
      <StripePaymentForm
        amountInCents={props.amountInCents}
        beforeSubmit={props.beforeSubmit}
        disabled={props.disabled}
        onBeforeConfirm={props.onBeforeConfirm}
        onPaymentElementUnavailable={props.onPaymentElementUnavailable}
        onPaymentSubmitted={props.onPaymentSubmitted}
        onSubmissionStateChange={props.onSubmissionStateChange}
      />
    </CheckoutElementsProvider>
  );
};

export default StripeCheckoutSessionPaymentElement;
